import { useConfirm } from "@/components/shared/ConfirmProvider";
import { Head, Link, router } from "@inertiajs/react";
import { UserCheck, School as SchoolIcon, Check, X } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const ROLE_TABS = [
  { key: "all",          label: "All" },
  { key: "school_admin", label: "School admins" },
  { key: "teacher",      label: "Teachers" },
  { key: "parent",       label: "Parents" },
];

const ROLE_BADGE_VARIANT = {
  school_admin: "default",
  teacher: "accent",
  parent: "secondary",
};

export default function Index({ groups, filters, schools, total_users }) {
  const confirm = useConfirm();
  const setFilter = (patch) => {
    const next = { role: filters.role, school_id: filters.school_id, ...patch };
    const query = {};
    if (next.role && next.role !== "all") query.role = next.role;
    if (next.school_id) query.school_id = next.school_id;
    router.get("/admin/users", query, { preserveScroll: true, preserveState: true });
  };

  return (
    <AdminLayout
      eyebrow="Support"
      title="Users"
    >
      <Head title="Admin · Users" />

      <p className="text-sm text-muted-foreground -mt-6 mb-6">
        Find a school admin, teacher, or parent and sign in as them to debug or guide them through the app.
      </p>

      {/* ── Filters ────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 mb-6 border-b border-border pb-4">
        <div className="flex flex-wrap gap-2">
          {ROLE_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilter({ role: tab.key })}
              className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filters.role === tab.key
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary/60 text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <label htmlFor="school-filter" className="text-xs uppercase tracking-wider text-muted-foreground">
            School
          </label>
          <select
            id="school-filter"
            value={filters.school_id || ""}
            onChange={(e) => setFilter({ school_id: e.target.value || null })}
            className="rounded-md border border-input bg-card px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="">All schools</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-6">
        {total_users} {total_users === 1 ? "user" : "users"} across {groups.length} {groups.length === 1 ? "school" : "schools"}.
      </p>

      {/* ── Grouped results ────────────────────────────── */}
      {groups.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-muted-foreground">No users match these filters.</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <Card key={group.school_id} className="overflow-hidden">
              <header className="flex items-center gap-2 px-5 py-3 border-b border-border bg-background">
                <SchoolIcon className="h-4 w-4 text-muted-foreground" />
                <h2 className="font-display text-base font-semibold text-foreground">
                  {group.school_name}
                </h2>
                <span className="text-xs text-muted-foreground">
                  · {group.users.length} {group.users.length === 1 ? "user" : "users"}
                </span>
              </header>
              <table className="w-full text-sm">
                <thead className="bg-background/60 border-b border-border">
                  <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-5 py-2 font-medium">User</th>
                    <th className="text-left px-5 py-2 font-medium">Role</th>
                    <th
                      className="text-center px-3 py-2 font-medium"
                      title="Receives campaign update emails"
                    >
                      Updates
                    </th>
                    <th
                      className="text-center px-3 py-2 font-medium"
                      title="Receives digest emails"
                    >
                      Digests
                    </th>
                    <th className="text-right px-5 py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {group.users.map((row) => (
                    <tr
                      key={`${row.user_id}-${row.role}`}
                      className="hover:bg-secondary/40 transition-colors"
                    >
                      <td className="px-5 py-3">
                        <p className="font-medium text-foreground">{row.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {row.email}
                          {!row.email_verified && (
                            <span className="ml-2 text-warning-foreground">unverified</span>
                          )}
                        </p>
                      </td>
                      <td className="px-5 py-3">
                        <Badge variant={ROLE_BADGE_VARIANT[row.role] || "outline"}>
                          {row.role_label}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <PrefIcon enabled={row.email_prefs.campaign_updates} />
                      </td>
                      <td className="px-3 py-3 text-center">
                        <PrefIcon enabled={row.email_prefs.digests} />
                      </td>
                      <td className="px-5 py-3 text-right">
                        {row.impersonate_path ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              const ok = await confirm({
                                title: `Sign in as ${row.name}?`,
                                body: `You'll see PagePledge as ${row.email} does until you stop impersonating.`,
                                confirmLabel: "Sign in as them",
                              });
                              if (ok) router.post(row.impersonate_path);
                            }}
                          >
                            <UserCheck className="h-4 w-4" />
                            Impersonate
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

function PrefIcon({ enabled }) {
  return enabled ? (
    <Check className="h-4 w-4 text-success inline-block" aria-label="Subscribed" />
  ) : (
    <X className="h-4 w-4 text-muted-foreground inline-block" aria-label="Unsubscribed" />
  );
}
